import {
  computed,
  onMounted,
  reactive,
  useContext,
} from "@nuxtjs/composition-api";
import REQUEST_CALL_MUTATION from "~/graphql/mutations/requestCallFromCustomerService.graphql";
import { useCallbackRequestStore } from "~/stores/callbackRequest";

export const CALLBACK_SOURCE = {
  REGISTRATION: "registration",
  ACCOUNT_HEADER: "account_header",
  SUBSCRIPTION_PAGE: "subscription_page",
  WAITLIST: "waitlist",
};

export function useRequestCallback() {
  const context = useContext();
  const { $mutate, $joszaki, $sentry, $track, i18n } = context;
  const callbackRequestStore = useCallbackRequestStore();

  const state = reactive({
    loading: false,
    requested: computed(() => callbackRequestStore.isOnCooldown),
    availableAgainText: computed(() =>
      callbackRequestStore.isOnCooldown
        ? i18n.t("requestCallback.availableAgainAt", {
            date: i18n.d(
              new Date(callbackRequestStore.availableAt),
              "shortDateTime"
            ),
          })
        : null
    ),
  });

  onMounted(() => callbackRequestStore.load(context));

  async function sendCallbackRequest(source) {
    if (state.loading || state.requested) {
      return;
    }
    try {
      state.loading = true;

      const resp = await $mutate(REQUEST_CALL_MUTATION, { source });

      if (!resp?.ok) {
        throw new Error(
          "requestCallFromCustomerService did not return success"
        );
      }

      callbackRequestStore.markRequested();
      $joszaki.toast({
        message: i18n.t("requestCallback.successToast"),
        type: "success",
      });
      $track.analyticsEvent({
        category: "mate",
        action: "request-call-from-customer-service",
        label: source,
      });
    } catch (err) {
      await callbackRequestStore.load(context, { force: true });
      if (callbackRequestStore.isOnCooldown) {
        $joszaki.toast({
          message: i18n.t("requestCallback.alreadyRequested"),
          type: "warning",
        });
        return;
      }
      $joszaki.toast({
        message: i18n.t("requestCallback.error"),
        type: "error",
      });
      $sentry.captureException(err, {
        tags: { registration: "request-call" },
      });
    } finally {
      state.loading = false;
    }
  }

  function requestCallback(source) {
    $joszaki.confirm({
      title: i18n.t("requestCallback.confirmTitle"),
      message: i18n.t("requestCallback.confirmMessage"),
      confirmText: i18n.t("requestCallback.confirmYes"),
      cancelText: i18n.t("common.cancel"),
      type: "info",
      onConfirm: () => sendCallbackRequest(source),
    });
  }

  return { state, requestCallback, sendCallbackRequest, CALLBACK_SOURCE };
}

export default useRequestCallback;
